import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { OrderWorkflowService } from '../../shared/order-workflow/order-workflow.service';

export interface NavItem {
  label: string;
  link?: string;
  icon: string;
  visible?: boolean;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit, OnDestroy {
  private readonly allNavItems: NavItem[] = [
    { label: 'Dashboard', link: '/dashboard', icon: 'fas fa-chart-line', visible: true },
    { label: 'Masters', link: '/masters', icon: 'fas fa-layer-group', visible: false },
    { label: 'Primary Order', link: '/primary-order', icon: 'fas fa-shopping-cart', visible: false },
    { label: 'Sales Order', link: '/sales-order', icon: 'fas fa-file-invoice', visible: false },
    { label: 'Sales Invoice', link: '/sales-invoice', icon: 'fas fa-file-invoice-dollar', visible: false },
    //{ label: 'Customer Onboarding', link: '/customer-onboarding', icon: 'fas fa-user-plus', visible: true },
    { label: 'Storage Location', link: '/storage-location', icon: 'fas fa-warehouse', visible: true },
    { label: 'Stock Transfer', link: '/stock-transfer', icon: 'fas fa-right-left', visible: true },
    { label: 'GRN', link: '/invoice', icon: 'fas fa-file-invoice', visible: true },
    { label: 'Self Ledger', link: '/ledger', icon: 'fas fa-book', visible: true },
    { label: 'Closing Stock', link: '/closing-stock', icon: 'fas fa-cubes', visible: true },
    { label: 'Bulk Upload', link: '/bulk-upload', icon: 'fas fa-file-upload', visible: false },
    { label: 'Forecast', link: '/monthly-forecast', icon: 'fas fa-chart-simple', visible: true },
    { label: 'Loyalty Points', link: '/loyalty-points', icon: 'fas fa-coins', visible: true },
    { label: 'Reports', link: '/reports', icon: 'fas fa-chart-bar', visible: true },
  ];
  navItems: NavItem[] = [];

  private destroy$ = new Subject<void>();

  private get customerCode(): string {
    return (localStorage.getItem('customerCode') || localStorage.getItem('customerCode1') || '').toString().trim();
  }

  private get isMasterUser(): boolean {
    return this.customerCode === '1000000001';
  }

  constructor(
    private router: Router,
    private orderWorkflowService: OrderWorkflowService
  ) { }

  ngOnInit(): void {
    this.updateNavItems();
    this.loadMenuPermissions();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isReportsActive(): boolean {
    return this.router.url.startsWith('/reports') || this.router.url.startsWith('/report/');
  }

  private updateNavItems(permissions?: { primaryOrder?: boolean; salesOrder?: boolean; salesInvoice?: boolean }): void {
    const isMaster = this.isMasterUser;
    const { primaryOrder, salesOrder, salesInvoice } = permissions || {};

    this.navItems = this.allNavItems.filter((item) => {
      if (item.label === 'Masters' || item.label === 'Bulk Upload') {
        return isMaster;
      }

      if (item.label === 'Primary Order') {
        return !!primaryOrder;
      }

      if (item.label === 'Sales Order') {
        return !!salesOrder;
      }

      if (item.label === 'Sales Invoice') {
        return !!salesInvoice;
      }

      return item.visible;
    });
  }

  private loadMenuPermissions(): void {
    if (!this.customerCode) {
      this.updateNavItems();
      return;
    }

    this.orderWorkflowService.getUserProfile(this.customerCode).subscribe({
      next: (response: any) => {
        if (!response?.success || !response?.result) {
          this.updateNavItems();
          return;
        }

        const { primaryOrder, salesOrder, salesInvoice } = response.result;
        this.updateNavItems({ primaryOrder, salesOrder, salesInvoice });
      },
      error: (error: any) => {
        console.error('Failed to load sidebar permissions:', error);
        this.updateNavItems();
      }
    });
  }
}
